import type { TemplateNode } from '@gymcloud/mixedscript'

import { ChevronLeftIcon, ChevronRightIcon } from '~/components/icons'
import { cycleOf, dayOf } from '~/lib/template-tree'
import { cycleStartColumn, weekOrder } from '~/lib/weekdays'

/** A cycle longer than this is not a picture any more, and the prompt it sits in has an end. */
const WEEKS_SHOWN = 6

/** The days of a cycle that carry a workout, by the day they sit on. */
function workoutsByDay(node: TemplateNode, cycle: number): Map<number, string> {
  const named = new Map<number, string>()
  for (const workout of node.workouts) {
    const day = dayOf(workout)
    if (day !== null && day >= 0 && day < cycle) {
      named.set(day, workout.name)
    }
  }
  return named
}

const count = (many: number, one: string) => `${String(many)} ${many === 1 ? one : `${one}s`}`

/** One visual step through the local week, wrapping at either edge. */
function movedAnchor(anchor: number, by: -1 | 1): number {
  const week = weekOrder()
  const found = week.findIndex(({ weekday }) => weekday === anchor)
  const at = found < 0 ? 0 : found
  return week[(at + by + week.length) % week.length]?.weekday ?? anchor
}

/**
 * What the cycle is, in a sentence. The numbers a template holds you to — how long it runs, how
 * much of it is training and how much is rest — read as prose rather than as a row of statistics,
 * with the ones this template supplies set apart from the words around them.
 *
 * Which weekday it begins on is not in here. The heading asks it and the drawing answers it, and
 * a sentence saying it a third time would be the longest of the three and the easiest to get
 * wrong: a cycle that is not seven days long does not begin on the same weekday twice.
 */
export function CycleSentence({ node }: { node: TemplateNode }) {
  const cycle = Math.max(1, cycleOf(node) ?? 7)
  const workouts = workoutsByDay(node, cycle).size
  const rest = Math.max(0, cycle - workouts)

  return (
    <p className="text-sm text-ink-muted">
      A <Moving>{String(cycle)}-day</Moving> cycle of <Moving>{count(workouts, 'workout')}</Moving>
      {rest > 0 ? (
        <>
          {' '}
          and <Moving>{count(rest, 'rest day')}</Moving>
        </>
      ) : null}
      .
    </p>
  )
}

/** The part of a sentence that is this template's rather than any template's. */
const Moving = ({ children }: { children: React.ReactNode }) => (
  <strong className="font-semibold text-ink">{children}</strong>
)

/**
 * The cycle drawn on the weeks it runs across — a column per weekday, each holding the days of
 * the cycle that fall on it — and the drawing is the choice of which weekday it begins on.
 * "Which weekday does this begin on" is a question about a shape, so the shape is where it is
 * answered: a whole column is one control, and pressing anywhere down it moves the cycle under
 * that day.
 *
 * A day the cycle does not reach is left empty rather than dropped, so the run-up to the first
 * session and the tail after the last one read as the calendar they are. See
 * `/specs/features/workout.spec.md`, The templates page.
 */
export function CycleWeeks({
  node,
  anchor,
  onAnchor,
  heading,
}: {
  node: TemplateNode
  anchor: number
  onAnchor: (weekday: number) => void
  /** Left off where whatever the drawing sits under has already asked the question. */
  heading?: string
}) {
  const cycle = Math.max(1, cycleOf(node) ?? 7)
  const start = cycleStartColumn(anchor)
  /*
   * The room the worst weekday would need — the last column of the week, which pushes the cycle
   * furthest down. Every column is this tall whatever is chosen, so picking a day does not
   * resize the drawing and move the columns out from under the finger choosing with them. The
   * spare days are drawn as nothing rather than as rest: a cycle ending on Sunday has no eighth
   * day to rest on, and a blank cell would say it had.
   */
  const rows = Math.min(WEEKS_SHOWN, Math.ceil((6 + cycle) / 7))
  const named = workoutsByDay(node, cycle)

  return (
    <div className="space-y-2">
      {heading === undefined ? null : (
        <h3 className="text-xs font-medium tracking-wide text-ink-muted uppercase">{heading}</h3>
      )}
      <div role="group" aria-label="Cycle weekday controls" className="flex items-center gap-2">
        <button
          type="button"
          aria-label="Move cycle one weekday earlier"
          className="shrink-0 rounded-full p-2 text-ink-muted transition-colors hover:bg-hover hover:text-ink"
          onClick={() => {
            onAnchor(movedAnchor(anchor, -1))
          }}
        >
          <ChevronLeftIcon aria-hidden className="size-5" />
        </button>
        <div
          role="radiogroup"
          aria-label="The cycle on a week"
          className="grid min-w-0 flex-1 grid-cols-7 gap-0.5 select-none"
        >
          {weekOrder().map(({ weekday, name }, column) => {
            const days = Array.from({ length: rows }, (_, row) => {
              const day = row * 7 + column - start
              return day < 0 || day >= cycle ? null : (named.get(day) ?? '')
            })
            // What the column holds, so choosing by ear says the same as choosing by eye.
            const holds = days.filter((day): day is string => day !== null && day.length > 0)
            const chosen = weekday === anchor

            return (
              <button
                key={weekday}
                type="button"
                role="radio"
                aria-checked={chosen}
                aria-label={holds.length === 0 ? name : `${name}: ${holds.join(', ')}`}
                onClick={() => {
                  onAnchor(weekday)
                }}
                /*
                 * The ground under a whole column says "this is one control, and the pointer is
                 * on it" — which is about the pointer, not about the answer. What was chosen is
                 * said by the heading alone, so the two never have to be told apart.
                 */
                className="flex flex-col gap-1 rounded-xl p-1 transition-colors hover:bg-hover"
              >
                <span
                  aria-hidden
                  className={`truncate rounded-lg py-1 text-center text-xs ${
                    chosen ? 'bg-accent font-semibold text-on-accent' : 'font-medium text-ink-muted'
                  }`}
                >
                  {name}
                </span>
                {days.map((day, row) => (
                  <span
                    key={row}
                    aria-hidden
                    className={
                      day === null
                        ? 'h-9 rounded-lg bg-surface-bright/40'
                        : day === ''
                          ? 'h-9 rounded-lg bg-surface-bright'
                          : 'grid h-9 place-items-center overflow-hidden rounded-lg bg-accent/15 px-1 text-[0.625rem] leading-tight font-medium text-accent'
                    }
                  >
                    <span className="line-clamp-2 text-center">{day}</span>
                  </span>
                ))}
              </button>
            )
          })}
        </div>
        <button
          type="button"
          aria-label="Move cycle one weekday later"
          className="shrink-0 rounded-full p-2 text-ink-muted transition-colors hover:bg-hover hover:text-ink"
          onClick={() => {
            onAnchor(movedAnchor(anchor, 1))
          }}
        >
          <ChevronRightIcon aria-hidden className="size-5" />
        </button>
      </div>
    </div>
  )
}
